/**
 * Risk Posture Dashboard Component
 * Unified dashboard displaying combined IARPM risk metrics from all three AI agents:
 * - Privilege Creep Detection
 * - Dormant User Detection
 * - Excessive Access Detection
 *
 * Features:
 * - Overall Risk Posture Score (calculated)
 * - Summary cards for each risk type
 * - Top high-risk users across all categories
 * - Risk distribution visualization
 * - Quick navigation to detailed dashboards
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Button,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Stack,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useAuth0 } from '@auth0/auth0-react';
import { useUserMetadata } from '../../../context/UserMetadataContext';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import ShieldIcon from '@mui/icons-material/Shield';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ActivityIcon from '@mui/icons-material/Timeline';
import GroupIcon from '@mui/icons-material/Group';
import LockIcon from '@mui/icons-material/Lock';
import apiService from '../services/apiService';

// Risk level colors
const RISK_COLORS = {
  CRITICAL: '#dc3545',
  HIGH: '#fd7e14',
  MODERATE: '#ffc107',
  LOW: '#20c997',
  ACCEPTABLE: '#28a745',
};

const RiskPostureDashboard = ({ onNavigateToTab }) => {
  const { getAccessTokenSilently } = useAuth0();
  const { userMetadata } = useUserMetadata();

  // Loading states
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Data states
  const [privilegeCreepData, setPrivilegeCreepData] = useState(null);
  const [dormantUserData, setDormantUserData] = useState(null);
  const [excessiveAccessData, setExcessiveAccessData] = useState(null);
  const [trustDegradationData, setTrustDegradationData] = useState(null);

  // Derived states
  const [overallRiskScore, setOverallRiskScore] = useState(0);
  const [riskDistribution, setRiskDistribution] = useState([]);
  const [topRiskUsers, setTopRiskUsers] = useState([]);

  const tenantName = userMetadata?.company || 'trustx';
  const baseUrl = process.env.REACT_APP_API_URL || 'https://api-dev.trustx.cloud/profile360';

  // Initialize API service
  const initializeApi = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      apiService.setAuthToken(token);
      apiService.setTenant(tenantName);
      return true;
    } catch (err) {
      console.error('Failed to initialize API service:', err);
      setError('Failed to authenticate. Please try logging in again.');
      return false;
    }
  }, [getAccessTokenSilently, tenantName]);

  // Fetch all risk data
  const fetchAllRiskData = useCallback(async () => {
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Tenant-Name': tenantName,
      };

      // Fetch all risk data in parallel (summaries + user lists)
      const [pcSummaryRes, pcUsersRes, duResponse, eaSummaryRes, eaUsersRes, trustDegradationRes] = await Promise.all([
        fetch(`${baseUrl}/api/privilege-creep/summary`, { headers }),
        fetch(`${baseUrl}/api/privilege-creep/insights?limit=50`, { headers }),
        apiService.getAIDormantInsights({ page: 1, pageSize: 100 }),
        fetch(`${baseUrl}/api/excessive-access/summary`, { headers }),
        fetch(`${baseUrl}/api/excessive-access/insights?limit=50&sort_by=risk_score&sort_order=desc`, { headers }),
        apiService.getTrustDegradationSummary(),
      ]);

      // Parse privilege creep data
      const pcSummaryData = await pcSummaryRes.json();
      const pcUsersData = await pcUsersRes.json();
      if (pcSummaryData.success) {
        const pcUsers = pcUsersData.success ? pcUsersData.data : [];
        setPrivilegeCreepData({
          ...pcSummaryData.data,
          users: pcUsers,
        });
      }

      // Parse dormant user data
      if (duResponse.success) {
        const dormantUsers = duResponse.data?.data || [];
        const total = duResponse.data?.total || 0;
        setDormantUserData({
          users: dormantUsers,
          total: total,
          highRisk: dormantUsers.filter((u) => u.risk_score >= 8).length,
          moderate: dormantUsers.filter((u) => u.risk_score >= 5 && u.risk_score < 8).length,
          low: dormantUsers.filter((u) => u.risk_score < 5).length,
        });
      }

      // Parse excessive access data
      const eaSummaryData = await eaSummaryRes.json();
      const eaUsersData = await eaUsersRes.json();
      if (eaSummaryData.success) {
        const eaUsers = eaUsersData.success ? (eaUsersData.data?.insights || []) : [];
        setExcessiveAccessData({
          ...eaSummaryData.data,
          users: eaUsers,
        });
      }

      // Parse trust degradation data
      if (trustDegradationRes.success && trustDegradationRes.data?.data) {
        setTrustDegradationData(trustDegradationRes.data.data);
      }
    } catch (err) {
      console.error('Error fetching risk data:', err);
      setError(err.message);
    }
  }, [getAccessTokenSilently, tenantName, baseUrl]);

  // Calculate overall risk score and distributions
  useEffect(() => {
    if (!privilegeCreepData && !dormantUserData && !excessiveAccessData) return;

    // Calculate risk counts
    const pcSummary = privilegeCreepData?.summary || {};
    const eaSummary = excessiveAccessData || {};

    const critical =
      (pcSummary.critical?.count || 0) +
      (eaSummary.critical_count || 0);
    const high =
      (pcSummary.high?.count || 0) +
      (eaSummary.high_count || 0) +
      (dormantUserData?.highRisk || 0);
    const moderate =
      (pcSummary.moderate?.count || 0) +
      (eaSummary.moderate_count || 0) +
      (dormantUserData?.moderate || 0);
    const low =
      (pcSummary.low?.count || 0) +
      (eaSummary.acceptable_count || 0) +
      (dormantUserData?.low || 0);

    const totalUsers =
      (pcSummary.total_users || 0) +
      (dormantUserData?.total || 0) +
      (eaSummary.total_users || 0);

    // Calculate overall risk score (0-100)
    // Weight critical issues heavily
    const riskScore =
      totalUsers > 0
        ? Math.min(
            100,
            Math.round(
              ((critical * 40 + high * 25 + moderate * 10) / totalUsers) * 10
            )
          )
        : 0;
    setOverallRiskScore(riskScore);

    // Set risk distribution for chart
    setRiskDistribution([
      { name: 'Critical', value: critical, color: RISK_COLORS.CRITICAL },
      { name: 'High', value: high, color: RISK_COLORS.HIGH },
      { name: 'Moderate', value: moderate, color: RISK_COLORS.MODERATE },
      { name: 'Low/Acceptable', value: low, color: RISK_COLORS.ACCEPTABLE },
    ]);

    // Combine top risk users from all sources
    const allRiskUsers = [];

    // Add privilege creep users (API uses 'email' field)
    if (privilegeCreepData?.users && privilegeCreepData.users.length > 0) {
      allRiskUsers.push(
        ...privilegeCreepData.users
          .filter((u) => (u.risk_score || 0) >= 1)
          .slice(0, 5)
          .map((u) => ({
            email: u.email || u.user_email || u.name,
            display_name: u.display_name || u.name,
            job_title: u.job_title,
            risk_score: u.risk_score,
            classification: u.classification || 'HIGH_RISK',
            reasoning: u.reasoning || u.ai_reasoning,
            source: 'Privilege Creep',
            sourceIcon: 'shield',
          }))
      );
    }

    // Add dormant users (API uses 'user_email' field)
    if (dormantUserData?.users && dormantUserData.users.length > 0) {
      allRiskUsers.push(
        ...dormantUserData.users
          .filter((u) => (u.risk_score || 0) >= 1)
          .slice(0, 5)
          .map((u) => ({
            email: u.user_email || u.email,
            display_name: u.display_name,
            job_title: u.job_title,
            risk_score: u.risk_score,
            classification: u.classification || 'DORMANT',
            reasoning: u.reasoning,
            source: 'Dormant User',
            sourceIcon: 'user-x',
          }))
      );
    }

    // Add excessive access users (API uses 'user_email' field)
    if (excessiveAccessData?.users && excessiveAccessData.users.length > 0) {
      allRiskUsers.push(
        ...excessiveAccessData.users
          .filter((u) => (u.risk_score || 0) >= 1)
          .slice(0, 5)
          .map((u) => ({
            email: u.user_email || u.email || u.display_name,
            display_name: u.display_name,
            job_title: u.job_title,
            risk_score: u.risk_score,
            classification: u.classification || 'EXCESSIVE',
            reasoning: u.reasoning || `Z-score: ${u.group_count_z_score?.toFixed(2) || 'N/A'}`,
            source: 'Excessive Access',
            sourceIcon: 'trending-up',
          }))
      );
    }

    // Sort by risk score and take top 10
    allRiskUsers.sort((a, b) => (b.risk_score || 0) - (a.risk_score || 0));
    setTopRiskUsers(allRiskUsers.slice(0, 10));
  }, [privilegeCreepData, dormantUserData, excessiveAccessData]);

  // Initial data load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const initialized = await initializeApi();
      if (initialized) {
        await fetchAllRiskData();
      }
      setLoading(false);
    };
    loadData();
  }, [initializeApi, fetchAllRiskData]);

  // Refresh handler
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllRiskData();
    setRefreshing(false);
  };

  // Get risk level text
  const getRiskLevelText = (score) => {
    if (score >= 80) return 'Critical';
    if (score >= 60) return 'High';
    if (score >= 40) return 'Moderate';
    if (score >= 20) return 'Low';
    return 'Minimal';
  };

  // Get MUI color for risk score
  const getRiskColor = (score) => {
    if (score >= 80) return 'error';
    if (score >= 60) return 'warning';
    if (score >= 40) return 'info';
    return 'success';
  };

  if (loading) {
    return (
      <Box sx={{ py: 5, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }} color="text.secondary">
          Loading Risk Posture Dashboard...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error">
          <Typography variant="subtitle2" fontWeight={700}>
            Error Loading Dashboard
          </Typography>
          <Typography variant="body2">{error}</Typography>
        </Alert>
      </Box>
    );
  }

  const pcSummary = privilegeCreepData?.summary || {};
  const eaSummary = excessiveAccessData || {};

  /* ── Section-title style (matches Overview.js) ──────────────── */
  const sectionTitle = {
    fontSize: '0.8rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: 'text.primary',
  };

  const riskScoreColor =
    overallRiskScore >= 60
      ? RISK_COLORS.HIGH
      : overallRiskScore >= 40
      ? RISK_COLORS.MODERATE
      : RISK_COLORS.ACCEPTABLE;

  return (
    <Box>
      {/* Refresh bar */}
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={
            refreshing
              ? <CircularProgress size={14} color="inherit" />
              : <RefreshIcon />
          }
          onClick={handleRefresh}
          disabled={refreshing}
          size="small"
        >
          Refresh
        </Button>
      </Stack>

      {/* ── Row 1: Risk Score + Distribution (equal 6/6) ────────── */}
      <Grid2 container spacing={2} sx={{ mb: 2 }}>
        <Grid2 xs={12} md={6}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={sectionTitle} gutterBottom>
                Overall Risk Score
              </Typography>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  width: 100,
                  height: 100,
                  my: 1.5,
                  border: '4px solid',
                  borderColor: riskScoreColor,
                }}
              >
                <Typography variant="h3" fontWeight={700} sx={{ color: riskScoreColor }}>
                  {overallRiskScore}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={overallRiskScore}
                color={getRiskColor(overallRiskScore)}
                sx={{ width: '80%', mb: 1.5, height: 6, borderRadius: 3 }}
              />
              <Chip
                label={`${getRiskLevelText(overallRiskScore)} Risk`}
                color={getRiskColor(overallRiskScore)}
                size="small"
                sx={{ fontWeight: 600 }}
              />
            </CardContent>
          </Card>
        </Grid2>

        <Grid2 xs={12} md={6}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography sx={sectionTitle} gutterBottom>
                Risk Distribution
              </Typography>
              <Box sx={{ flex: 1, minHeight: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={riskDistribution}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={70}
                      label={({ name, value }) => (value > 0 ? `${name}: ${value}` : '')}
                    >
                      {riskDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid2>
      </Grid2>

      {/* ── Row 2: Risk Type Summary Cards (equal thirds) ────── */}
      <Grid2 container spacing={2} sx={{ mb: 2 }}>
        {/* Privilege Creep Card */}
        <Grid2 xs={12} md={4}>
          <Card
            sx={{ height: '100%', cursor: 'pointer', display: 'flex', flexDirection: 'column', '&:hover': { boxShadow: 4 } }}
            onClick={() => onNavigateToTab && onNavigateToTab(1)}
          >
            <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <ShieldIcon sx={{ color: 'error.main', fontSize: 20 }} />
                  <Typography variant="subtitle2" fontWeight={700}>Privilege Creep</Typography>
                </Stack>
                <ChevronRightIcon sx={{ color: 'text.disabled', fontSize: 18 }} />
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                Users with excessive group/app assignments
              </Typography>

              <Grid2 container spacing={0} textAlign="center" sx={{ flex: 1 }}>
                <Grid2 xs={3}>
                  <Typography variant="h6" fontWeight={700} color="error.main">
                    {pcSummary.critical?.count || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Critical</Typography>
                </Grid2>
                <Grid2 xs={3}>
                  <Typography variant="h6" fontWeight={700} color="warning.main">
                    {pcSummary.high?.count || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">High</Typography>
                </Grid2>
                <Grid2 xs={3}>
                  <Typography variant="h6" fontWeight={700} color="info.main">
                    {pcSummary.moderate?.count || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Moderate</Typography>
                </Grid2>
                <Grid2 xs={3}>
                  <Typography variant="h6" fontWeight={700} color="success.main">
                    {pcSummary.low?.count || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Low</Typography>
                </Grid2>
              </Grid2>

              <Divider sx={{ my: 1 }} />
              <Stack direction="row" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <GroupIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {pcSummary.total_users || 0} users
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">AI-powered</Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid2>

        {/* Dormant Users Card */}
        <Grid2 xs={12} md={4}>
          <Card
            sx={{ height: '100%', cursor: 'pointer', display: 'flex', flexDirection: 'column', '&:hover': { boxShadow: 4 } }}
            onClick={() => onNavigateToTab && onNavigateToTab(4)}
          >
            <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PersonOffIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                  <Typography variant="subtitle2" fontWeight={700}>Dormant Users</Typography>
                </Stack>
                <ChevronRightIcon sx={{ color: 'text.disabled', fontSize: 18 }} />
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                Inactive accounts with active access
              </Typography>

              <Grid2 container spacing={0} textAlign="center" sx={{ flex: 1 }}>
                <Grid2 xs={4}>
                  <Typography variant="h6" fontWeight={700} color="error.main">
                    {dormantUserData?.highRisk || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">High Risk</Typography>
                </Grid2>
                <Grid2 xs={4}>
                  <Typography variant="h6" fontWeight={700} color="warning.main">
                    {dormantUserData?.moderate || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Moderate</Typography>
                </Grid2>
                <Grid2 xs={4}>
                  <Typography variant="h6" fontWeight={700} color="success.main">
                    {dormantUserData?.low || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Low</Typography>
                </Grid2>
              </Grid2>

              <Divider sx={{ my: 1 }} />
              <Stack direction="row" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <GroupIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {dormantUserData?.total || 0} users
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">AI-powered</Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid2>

        {/* Excessive Access Card */}
        <Grid2 xs={12} md={4}>
          <Card
            sx={{ height: '100%', cursor: 'pointer', display: 'flex', flexDirection: 'column', '&:hover': { boxShadow: 4 } }}
            onClick={() => onNavigateToTab && onNavigateToTab(2)}
          >
            <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <TrendingUpIcon sx={{ color: 'info.main', fontSize: 20 }} />
                  <Typography variant="subtitle2" fontWeight={700}>Excessive Access</Typography>
                </Stack>
                <ChevronRightIcon sx={{ color: 'text.disabled', fontSize: 18 }} />
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                Access above peer cohort average
              </Typography>

              <Grid2 container spacing={0} textAlign="center" sx={{ flex: 1 }}>
                <Grid2 xs={3}>
                  <Typography variant="h6" fontWeight={700} color="error.main">
                    {eaSummary.critical_count || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Critical</Typography>
                </Grid2>
                <Grid2 xs={3}>
                  <Typography variant="h6" fontWeight={700} color="warning.main">
                    {eaSummary.high_count || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">High</Typography>
                </Grid2>
                <Grid2 xs={3}>
                  <Typography variant="h6" fontWeight={700} color="info.main">
                    {eaSummary.moderate_count || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Moderate</Typography>
                </Grid2>
                <Grid2 xs={3}>
                  <Typography variant="h6" fontWeight={700} color="success.main">
                    {eaSummary.acceptable_count || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">OK</Typography>
                </Grid2>
              </Grid2>

              <Divider sx={{ my: 1 }} />
              <Stack direction="row" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <GroupIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {eaSummary.total_users || 0} users
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">Z-score analysis</Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid2>
      </Grid2>

      {/* ── Row 3: Trust Degradation (full width accent card) ─── */}
      <Card
        sx={{
          mb: 2,
          cursor: 'pointer',
          borderLeft: `3px solid ${RISK_COLORS.CRITICAL}`,
          '&:hover': { boxShadow: 4 },
        }}
        onClick={() => onNavigateToTab && onNavigateToTab(4)}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <TrendingDownIcon sx={{ color: 'error.main', fontSize: 20 }} />
              <Typography variant="subtitle2" fontWeight={700}>
                Trust Degradation Detection
              </Typography>
              <Chip label="P3" size="small" sx={{ fontWeight: 700, fontSize: '0.65rem', height: 20, bgcolor: 'error.main', color: '#fff' }} />
            </Stack>
            <ChevronRightIcon sx={{ color: 'text.disabled', fontSize: 18 }} />
          </Stack>

          <Grid2 container spacing={1} textAlign="center">
            <Grid2 xs={2}>
              <Typography variant="h6" fontWeight={700} color="error.main">
                {trustDegradationData?.by_trust_level?.CRITICAL?.count || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">Critical</Typography>
            </Grid2>
            <Grid2 xs={2}>
              <Typography variant="h6" fontWeight={700} color="warning.main">
                {trustDegradationData?.by_trust_level?.DEGRADED?.count || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">Degraded</Typography>
            </Grid2>
            <Grid2 xs={2}>
              <Typography variant="h6" fontWeight={700} color="info.main">
                {trustDegradationData?.by_trust_level?.FAIR?.count || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">Fair</Typography>
            </Grid2>
            <Grid2 xs={2}>
              <Typography variant="h6" fontWeight={700} color="success.main">
                {trustDegradationData?.by_trust_level?.GOOD?.count || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">Good</Typography>
            </Grid2>
            <Grid2 xs={2}>
              <Typography variant="h6" fontWeight={700} color="primary.main">
                {trustDegradationData?.by_trust_level?.EXCELLENT?.count || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">Excellent</Typography>
            </Grid2>
            <Grid2 xs={2}>
              <Typography variant="h6" fontWeight={700} color="text.secondary">
                {trustDegradationData?.by_trend?.DEGRADING || 0}
              </Typography>
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                <TrendingDownIcon sx={{ fontSize: 12, color: 'error.main' }} />
                <Typography variant="caption" color="text.secondary">Degrading</Typography>
              </Stack>
            </Grid2>
          </Grid2>

          <Divider sx={{ my: 1 }} />
          <Stack direction="row" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <GroupIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {trustDegradationData?.total_users || 0} users
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Avg: {trustDegradationData?.average_trust_score || 0}/100
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <ActivityIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {trustDegradationData?.high_velocity_users || 0} high velocity
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* ── Row 4: Top Risk Users + Breakdown (equal 6/6) ────── */}
      <Grid2 container spacing={2} sx={{ mb: 2 }}>
      <Grid2 xs={12} md={7}>
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ p: 2.5, pb: 0 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <WarningAmberIcon sx={{ color: 'error.main', fontSize: 20 }} />
              <Typography sx={sectionTitle}>Top Risk Users</Typography>
            </Stack>
            <Chip
              label={`${topRiskUsers.length} users`}
              color="error"
              size="small"
              sx={{ fontWeight: 600, height: 22 }}
            />
          </Stack>
        </CardContent>
        {topRiskUsers.length === 0 ? (
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <LockIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">No high-risk users detected</Typography>
            <Typography variant="caption" color="text.disabled">
              Your organization's risk posture is healthy
            </Typography>
          </CardContent>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Risk Source</TableCell>
                  <TableCell>Classification</TableCell>
                  <TableCell>Risk Score</TableCell>
                  <TableCell>Key Concern</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topRiskUsers.map((user, index) => {
                  // Normalize risk score: Excessive Access uses 0-100 scale, others use 0-10
                  const normalizedScore =
                    user.source === 'Excessive Access' && user.risk_score > 10
                      ? Math.round(user.risk_score / 10)
                      : user.risk_score;
                  const scoreColor =
                    (normalizedScore || 0) >= 9
                      ? '#dc3545'
                      : (normalizedScore || 0) >= 7
                      ? '#fd7e14'
                      : (normalizedScore || 0) >= 5
                      ? '#0dcaf0'
                      : '#198754';

                  const sourceColor =
                    user.source === 'Privilege Creep'
                      ? 'error'
                      : user.source === 'Dormant User'
                      ? 'warning'
                      : 'info';

                  const classColor =
                    user.classification?.includes('HIGH') || user.classification?.includes('CRITICAL')
                      ? 'error'
                      : user.classification?.includes('MODERATE') || user.classification === 'DORMANT'
                      ? 'warning'
                      : 'default';

                  return (
                    <TableRow key={`${user.email || user.user_email}-${index}`} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {user.email || user.user_email}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.job_title || user.display_name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.source || 'Unknown'}
                          color={sourceColor}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.classification || 'N/A'}
                          color={classColor}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'inline-block',
                            bgcolor: scoreColor,
                            color: '#fff',
                            px: 1.5,
                            py: 0.25,
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            fontWeight: 700,
                          }}
                        >
                          {normalizedScore !== undefined && normalizedScore !== null
                            ? `${normalizedScore}/10`
                            : 'N/A'}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {user.reasoning?.substring(0, 100) ||
                            user.ai_reasoning?.substring(0, 100) ||
                            'AI analysis pending'}
                          {(user.reasoning?.length > 100 || user.ai_reasoning?.length > 100) && '...'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
      </Grid2>

      {/* Risk Breakdown by Type */}
      <Grid2 xs={12} md={5}>
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography sx={sectionTitle} gutterBottom>
            Risk Breakdown by Type
          </Typography>
          <Box sx={{ flex: 1, minHeight: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={[
                  {
                    name: 'Privilege Creep',
                    Critical: pcSummary.critical?.count || 0,
                    High: pcSummary.high?.count || 0,
                    Moderate: pcSummary.moderate?.count || 0,
                    Low: pcSummary.low?.count || 0,
                  },
                  {
                    name: 'Dormant Users',
                    Critical: 0,
                    High: dormantUserData?.highRisk || 0,
                    Moderate: dormantUserData?.moderate || 0,
                    Low: dormantUserData?.low || 0,
                  },
                  {
                    name: 'Excessive Access',
                    Critical: eaSummary.critical_count || 0,
                    High: eaSummary.high_count || 0,
                    Moderate: eaSummary.moderate_count || 0,
                    Low: eaSummary.acceptable_count || 0,
                  },
                ]}
                layout="vertical"
                margin={{ top: 10, right: 20, left: 80, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <YAxis dataKey="name" type="category" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Critical" stackId="a" fill={RISK_COLORS.CRITICAL} />
                <Bar dataKey="High" stackId="a" fill={RISK_COLORS.HIGH} />
                <Bar dataKey="Moderate" stackId="a" fill={RISK_COLORS.MODERATE} />
                <Bar dataKey="Low" stackId="a" fill={RISK_COLORS.ACCEPTABLE} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>
      </Grid2>
      </Grid2>
    </Box>
  );
};

export default RiskPostureDashboard;
